<template>
  <div class="analytics-photo-gallery">
    <BackButton />
    <Card class="p-10">
      <div v-if="displayPhotos.length" class="my-2 flex flex-wrap justify-start">
        <div
          v-for="(photo, key) in displayPhotos"
          :key="key"
          class="w-1/5 px-1"
        >
          <ImageAsset
            v-if="photo"
            :key="photo.uuid"
            :photo="photo"
            class="max-h-160px image-bg-cover-no-repeat rounded my-2 cursor-pointer"
            @click="showImage(photo)"
          />
          <div v-else class="h-160px rounded my-2 bg-30 flex w-full">
            <Loader class="self-center" />
          </div>
        </div>
      </div>
      <div v-else-if="loading" class="h-160px rounded my-2 bg-30 flex w-full">
        <Loader class="self-center" />
      </div>
      <div v-else class="my-2">
        <p class="text-60">{{ $t('No photos') }}</p>
      </div>

      <pagination-links
        :has-previous-pages="hasPreviousPage"
        :has-more-pages="hasNextPage"
        @previous="selectPreviousPage"
        @next="selectNextPage"
      />
    </Card>

    <photo-modal
      v-if="showModal"
      :photo="modalPhoto"
      @close="closeModal"
    />
  </div>
</template>

<script>
  import _ from 'lodash';
  import Card from '@/_legacy/components/Card.vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import PhotoModal from '@/_legacy/components/Modals/PhotoModal.vue';
  import PaginationLinks from '@/_legacy/components/PaginationLinks.vue';
  import Modal from '@/_legacy/components/Modal.vue';
  import Paginatable from '@/_legacy/mixins/Paginatable';
  import InteractsWithQueryString from '@/_legacy/laravel-nova/InteractsWithQueryString';
  import { analyticsPhotos } from '@/services/api/resources/analytics';
  import Vue from 'vue';
  import { ImageAsset } from '@/ui/components/asset/ImageAsset';
  import { useTask } from '@/ui/concerns';
  import { BackButton } from '@/ui/components/button/BackButton';

  Vue.component('modal', Modal);

  // analytics, all photos display on checklist summary - all photos
  export default {
    name: 'PhotoGallery',
    components: {
      ImageAsset,
      Card,
      Loader,
      PaginationLinks,
      PhotoModal,
      BackButton
    },
    mixins: [
      Paginatable,
      InteractsWithQueryString,
    ],
    props: {
      question: {
        type: Object,
        default: () => ({}),
        required: true,
      },
    },
    data() {
      return {
        loading: false,
        showModal: false,
        modalPhoto: null,
        hasNextPage: false,
        hasPreviousPage: false,
        photosArrayResolved: [],
      };
    },
    computed: {
      photosArray() {
        return this.question.photos
          .map(questionsPhoto => {
            const formattedPhotoRecord = {
              drawing_id: questionsPhoto.drawing_v2_id,
              image_id: questionsPhoto.photo_v2_id,
              image: questionsPhoto.photo_v1_id.toString(),
              uuid: questionsPhoto.inspection_question_uuid,
            };

            return formattedPhotoRecord;
          });
      },
      offset() {
        return this.page === 1 ? 0 : (this.page - 1) * this.perPage;
      },
      displayPhotos() {
        const photos = _.filter(this.photosArrayResolved);
        return _.drop(photos, this.offset)
          .slice(0, this.perPage);
      },
      query() {
        return {
          ...{ question: this.question.inspection_question_uuid },
          ...this.page !== this.defaultPage && { [this.pageParameter]: this.page },
          ...this.perPage !== this.defaultPerPage && { [this.perPageParameter]: this.perPage },
        };
      },
      requestQueryString() {
        return {
          perPage: this.perPage,
          page: this.page,
        };
      },
    },
    watch: {
      photosArray: {
        handler: 'getPhotos',
        immediate: true,
      },
    },
    created() {
      this.initPagination();
    },
    methods: {
      initPagination() {
        this.initializePaginationFromQueryString();
        this.setPagination();
        this.$watch(
          () => ([
            this.page,
            this.perPage,
          ].join(',')),
          () => {
            this.replaceQueryString(this.query);
            this.setPagination();
          },
        );
      },
      showImage(photo) {
        this.showModal = true;
        this.modalPhoto = photo;
      },
      closeModal() {
        this.showModal = false;
        this.modalPhoto = null;
      },
      getPhotos() {
        return this.photosArray.forEach(photo => this.getPhoto(photo));
      },
      getPhoto(photo) {
        if (!photo) {
          return;
        }

        // has no v2 ids or only v2 drawing but image is binary
        if (!photo.image_id && !photo.drawing_id
          || !photo.image_id && photo.image) {
          this.loading = true;

          const getRecord = useTask(
            () => analyticsPhotos(photo.image),
            data => {
              const imageUrl = URL.createObjectURL(data.parsed);
              this.photosArrayResolved.push({
                ...photo,
                image: imageUrl,
              });

              this.loading = false;
            },
          );
          getRecord.run();
        } else {
          this.photosArrayResolved.push({ ...photo });
        }
      },
      setPagination() {
        this.hasPreviousPage = this.page && this.page > 1;
        this.hasNextPage = _.filter(this.photosArray).length - this.perPage * this.page > 0;
      },
    },
  };
</script>
